import React from "react";

import { Tooltip } from "./Tooltip";

import styles from "./InputWithIcon.module.scss";

type InputWithIconPosition = "right" | "left" | "none";

interface InputWithIconProps {
    /**
    * The optional text input ID.
    */
    id: string;

    /**
     * The optional id to link input to descriptive helper text.
     */
    errorMessageId?: string;

    /**
     * Indicates whether the input should display error styling.
     * If true, a red border is applied to the input field.
     */
    hasError?: boolean;

    /**
    * Placeholder text inside the input when empty.
    */
    placeholder?: string;

    /**
    * The value of the input.
    */
    value?: string;

    /**
    * Default value of the input.
    */
    defaultValue?: string;

    /**
    * Function triggered when the input value changes.
    */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

    /**
    * Callback function triggered when the input field loses focus.
    */
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

    /**
     * Whether to disable the text input.
     */
    disabled?: boolean;

    /**
     * Automatically focuses the input field when the component is mounted.
     */
    autoFocus?: boolean;

    /**
    * To indicate whether the field is required. If true, displays a red asterisk next to the label.
    */
    required?: boolean;

    /**
    * The tab order of the text input.
    */
    tabIndex?: number;

    /**
    * Maximum length of the input text.
    */
    maxLength?: number;

    /**
    * The name of the text input.
    */
    name?: string;

    /**
    * Icon to display in the input.
    */
    icon?: React.ReactNode;

    /**
    * The position of the icon: 'left' or 'right'.
    */
    iconPosition?: InputWithIconPosition;

    /**
     * Enables showing a tooltip with the input's value on hover.
     */
    useHoverToolTip?: boolean;

    /**
     * The input type
     */
    type?: "text" | "password";
}

/**
 * InputWithIcon Component
 * Renders an input field with optional left and right icons.
 */
function InputWithIcon({
    id,
    errorMessageId,
    hasError,
    placeholder,
    value,
    defaultValue,
    onChange,
    onBlur,
    disabled = false,
    autoFocus = false,
    required = false,
    tabIndex,
    maxLength,
    name,
    icon,
    iconPosition = "none",
    useHoverToolTip = false,
    type = "text"
}: Readonly<InputWithIconProps>): JSX.Element {
    return (
        <div
            className={`${styles.inputContainer}`}
        >
            {icon && iconPosition === "left" && (
                <div className={`${styles.icon} ${styles.leftIcon}`}>{icon}</div>
            )}
            <input
                id={id}
                placeholder={placeholder}
                value={value}
                defaultValue={defaultValue}
                onChange={onChange}
                onBlur={onBlur}
                disabled={disabled}
                autoFocus={autoFocus}
                required={required}
                className={`${styles.input} 
                ${icon && iconPosition === "left" ? styles.withLeftIcon : ""} 
                ${icon && iconPosition === "right" ? styles.withRightIcon : ""}
                ${hasError ? styles.withError : ""}`
                }
                aria-describedby={errorMessageId}
                tabIndex={tabIndex}
                maxLength={maxLength}
                name={name}
                type={type}
            />
            {icon && iconPosition === "right" && (
                <div className={`${styles.icon} ${styles.rightIcon}`}>{icon}</div>
            )}
            {useHoverToolTip && value && (
                <Tooltip place="bottom-end" anchorSelect={`#${id}`}><p>{value}</p></Tooltip>
            )}
        </div>
    );
}

export { InputWithIcon, InputWithIconPosition, InputWithIconProps };
